import React from 'react';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';
import withAttribute from './withAttribute';
import {iconName, typeName, qualify} from '@packages/systems/wfdl-Qualified';
import {Atom, withMeta, withSystem, Plugins} from '@packages/systems/wfdl';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CHECKOUT_ADDITIONAL_INFO,
} from '@packages/systems/commerce/constants';
import {
  CommerceCheckoutFormConstraint,
  SHARED_FORM_CONSTRAINTS_DESCENDANTS,
  SHARED_CHECKOUT_PAGE_SECTIONS_CONSTRAINTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import {TListOfElement, TTypeConstructor} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {
  EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
  getInPojo,
} from '@packages/utilities/data-utils';

const formPluginName = Plugins.name('Form');
const commerceElementToggleConstructor = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('SwitchLabelType'))
);

const CommerceCheckoutAdditionalInputsContainer = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const newContext = context
      .set(
        'includeTextInput',
        getBooleanOrElse(getInPojo(data, ['includeTextInput']), true)
      )
      .set(
        'includeTextArea',
        getBooleanOrElse(getInPojo(data, ['includeTextArea']), true)
      )
      .set(
        'includeCheckbox',
        getBooleanOrElse(getInPojo(data, ['includeCheckbox']), true)
      );

    return (
      <form {...generateAttributes(attributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </form>
    );
  }
);

const config = createIntegratedAtom({
  displayName: 'Additional Info',
  showEmpty: true,
  constraints: {
    ancestors: [
      // @ts-expect-error - TS2322 - Type '{ readonly is: readonly ["CommerceCheckoutFormContainer", "CommercePaypalCheckoutFormContainer"]; readonly name: "Checkout Form"; readonly rule: "ExactlyOne"; }' is not assignable to type 'RelationshipConstraint'.
      CommerceCheckoutFormConstraint,
      ...SHARED_CHECKOUT_PAGE_SECTIONS_CONSTRAINTS,
    ],
    descendants: [
      {is: 'CommerceCheckoutAdditionalTextArea', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutAdditionalTextInput', rule: 'ExactlyOne'},
      {is: 'CommerceCheckoutAdditionalCheckboxWrapper', rule: 'ExactlyOne'},
      ...SHARED_FORM_CONSTRAINTS_DESCENDANTS,
      ...SHARED_CHECKOUT_PAGE_SECTIONS_CONSTRAINTS,
    ],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          marginBottom: px(20),
        },
      },
    },
  },
  render: withAttribute(
    DATA_ATTR_NODE_TYPE,
    NODE_TYPE_COMMERCE_CHECKOUT_ADDITIONAL_INFO
  )(CommerceCheckoutAdditionalInputsContainer),
  fields: {
    includeTextInput: withMeta(
      {
        icon: qualify(formPluginName, iconName('Input')),
      },
      withSystem(
        {defaultValue: true, label: 'Text Field', labelVariant: 'hidden'},
        commerceElementToggleConstructor
      )
    ),
    includeTextArea: withMeta(
      {
        icon: qualify(formPluginName, iconName('Textarea')),
      },
      withSystem(
        {defaultValue: true, label: 'Textarea', labelVariant: 'hidden'},
        commerceElementToggleConstructor
      )
    ),
    includeCheckbox: withMeta(
      {
        icon: qualify(formPluginName, iconName('Checkbox')),
      },
      withSystem(
        {defaultValue: true, label: 'Checkbox', labelVariant: 'hidden'},
        commerceElementToggleConstructor
      )
    ),
    children: TListOfElement,
  },
});

export default config;
